import { AffiliationAvailabilityBoundary } from '@/hooks/use-affiliation-availability';
import { lazy, Suspense } from 'react';
import { parseAsString, parseAsStringEnum, useQueryState } from 'nuqs';
import { STEP_VALUES, type WizardStep } from './_hooks/useAffiliateImportWizard';

// Lazy-load the heavy modal content so the wizard bundle (papaparse + 5 step
// components + program-list query) is not shipped on every visit to the
// affiliates listing. The thin watcher below subscribes only to the `step`
// URL param; the content mounts (and the bundle loads) only when the user
// opens the wizard via AddAffiliateButton or via a deep link.
const AffiliateImportModalContent = lazy(() => import('./_components/AffiliateImportModalContent'));

interface AffiliateImportModalProps {
  presetProgramId?: string;
}

const AffiliateImportModal: React.FC<AffiliateImportModalProps> = ({ presetProgramId }) => {
  const [step, setStep] = useQueryState('step', parseAsStringEnum<WizardStep>(STEP_VALUES));
  const [programId] = useQueryState('program', parseAsString.withDefault(''));
  const [batchId] = useQueryState('job', parseAsString.withDefault(''));

  if (step === null) return null;

  const content = (
    <Suspense fallback={null}>
      <AffiliateImportModalContent presetProgramId={presetProgramId} />
    </Suspense>
  );
  // These two steps only observe an existing job (or cancel a paused one).
  // Their existing GET validates tenant ownership before showing any batch;
  // resetting removes `job` and returns through the new-import boundary.
  const readsExistingBatch =
    (step === 'importing' || step === 'done') && !!batchId && !!(programId || presetProgramId);
  return readsExistingBatch ? (
    content
  ) : (
    <AffiliationAvailabilityBoundary isOpen onClose={() => void setStep(null)}>
      {content}
    </AffiliationAvailabilityBoundary>
  );
};

export default AffiliateImportModal;
